import React from 'react';
import { Paper, Typography, Box, useTheme, CircularProgress, Alert } from '@mui/material';
import { BaseChart } from '@personal-dashboard/ui';
import { useQuery, gql } from '@apollo/client';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { parseBackendDate } from './utils';

dayjs.extend(isoWeek);

const GET_TASKS_HISTORY = gql`
  query GetTasksHistory {
    getTasks {
      id
      createdAt
      updatedAt
      status
      archived
    }
  }
`;

const BurndownChart: React.FC = () => {
  const theme = useTheme();
  const { data, loading, error } = useQuery(GET_TASKS_HISTORY, {
    fetchPolicy: 'cache-and-network'
  });

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed to load tasks: {error.message}</Alert>;

  const tasks = data?.getTasks || [];
  if (tasks.length === 0) return <Typography>No tasks found.</Typography>;
  const startOfWeek = dayjs().startOf('isoWeek');
  const endOfWeek = dayjs().endOf('isoWeek');
  const today = dayjs().endOf('day');

  // Days of the week for X-axis
  const weekDays = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
  const xAxisData = weekDays.map(d => d.format('ddd'));

  // 1. Calculate the total scope for this week
  // (Tasks that existed at any point during this week and were not archived before the week started)
  const activeWeekTasks = tasks.filter((t: any) => {
    const created = parseBackendDate(t.createdAt);
    const updated = parseBackendDate(t.updatedAt);
    if (!created) return false;

    const createdBeforeEnd = created.isBefore(endOfWeek);
    const notArchivedBeforeStart = !t.archived || (updated && updated.isAfter(startOfWeek));
    
    return createdBeforeEnd && notArchivedBeforeStart;
  });

  const totalTaskCount = activeWeekTasks.length;

  // 2. Calculate Actual Remaining Tasks per day (only up to today)
  const actualData = weekDays.map(day => {
    const dayEnd = day.endOf('day');
    
    // Only show data for days that have passed or are today
    if (day.isAfter(today)) return null;

    const remaining = activeWeekTasks.filter((t: any) => {
      const created = parseBackendDate(t.createdAt);
      const updated = parseBackendDate(t.updatedAt);
      
      // Task must have been created by the end of this day
      const exists = created && created.isBefore(dayEnd);
      
      // Task is "open" if it's not finished, or was finished AFTER this day
      const isFinished = t.status === 'FINISHED';
      const finishedAfterDay = updated && updated.isAfter(dayEnd);
      const isStillOpen = !isFinished || finishedAfterDay;

      // Task is not archived, or was archived AFTER this day
      const isArchived = t.archived;
      const archivedAfterDay = updated && updated.isAfter(dayEnd);
      const isNotArchivedYet = !isArchived || archivedAfterDay;

      return exists && isStillOpen && isNotArchivedYet;
    }).length;

    return remaining;
  });

  // 3. Calculate Ideal Burn Line
  // Connects (Monday, Total) to (Sunday, 0)
  const idealData = weekDays.map((_, index) => {
    return Math.max(0, totalTaskCount - (totalTaskCount / 6) * index).toFixed(1);
  });

  const option = {
    title: { 
      text: 'Sprint Burndown', 
      left: 'center', 
      textStyle: { fontSize: 16, fontWeight: 'bold', color: theme.palette.text.primary } 
    },
    tooltip: { 
      trigger: 'axis',
      backgroundColor: theme.palette.background.paper,
      textStyle: { color: theme.palette.text.primary },
      borderWidth: 1,
      borderColor: theme.palette.divider
    },
    legend: { 
      data: ['Actual', 'Ideal'], 
      bottom: 0,
      textStyle: { color: theme.palette.text.secondary }
    },
    grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData,
      axisLine: { lineStyle: { color: theme.palette.divider } },
      axisLabel: { color: theme.palette.text.secondary }
    },
    yAxis: {
      type: 'value',
      name: 'Tasks',
      minInterval: 1,
      splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      axisLabel: { color: theme.palette.text.secondary }
    },
    series: [
      {
        name: 'Ideal',
        type: 'line',
        data: idealData,
        symbol: 'none',
        lineStyle: { 
          color: theme.palette.text.disabled, 
          width: 2, 
          type: 'dashed' 
        },
        z: 1
      },
      {
        name: 'Actual',
        type: 'line',
        data: actualData,
        smooth: false,
        connectNulls: false,
        symbolSize: 8,
        itemStyle: { color: theme.palette.primary.main },
        lineStyle: { width: 4 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: theme.palette.primary.main + '44' },
              { offset: 1, color: theme.palette.primary.main + '00' }
            ]
          }
        },
        z: 2
      }
    ]
  };

  return (
    <Paper sx={{ p: 3, height: 400, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <BaseChart option={option} />
      </Box>
    </Paper>
  );
};

export default BurndownChart;
